import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  IconButton,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Stack,
  Grid,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigation } from "contexts/notificationsContext";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import {
  ISuperficie,
  ISelectOption,
  SuperficieTabIndex,
  grupoTemDescricao,
  GRUPO_TELA_RETRATIL,
  GRUPO_ESTEIRA_MONTADA,
} from "./types/superficie.types";
import {
  obterSuperficiePorId,
  salvarSuperficie,
  excluirSuperficie,
  duplicarSuperficie,
  listarReferencias,
  listarLinhas,
  listarGrupos,
  listarCores,
  listarUnidadesValorizacao,
  listarTiposTecido,
  validarCodigoUnico,
} from "./services/superficieService";
import { useValidacaoCodigoUnico } from "../hooks/useValidacaoCodigoUnico";
import { useSomenteLeituraN1 } from "../hooks/useSomenteLeituraN1";
import { AvisoPlanilhaPendenteN1 } from "../components/AvisoPlanilhaPendenteN1";
import { useFormularioAlterado } from "../hooks/useFormularioAlterado";
import { TabGeral } from "./components/TabGeral";
import { ModalGerenciamentoMidias } from "./components/ModalGerenciamentoMidias";
import { ModalConfirmacaoExcluir } from "./components/ModalConfirmacaoExcluir";
import { ModalHistorico } from "./components/ModalHistorico";

// FASE 05 — S3/PO 2026-05-13: IdTipoTela da Superficie (enum TipoTela = 4).
const ID_TIPO_TELA_SUPERFICIE = 4;

// Helper para formatar data de atualização
const formatarDataAtualizacao = (data?: string) => {
  if (!data) return "";
  try {
    const date = new Date(data);
    return `${date.toLocaleDateString("pt-BR")} - ${date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  } catch (e) {
    console.error("Erro ao formatar data:", e);
    return "";
  }
};

export const CadastroSuperficie: React.FC = () => {
  const navigate = useNavigate();
  const { id: idSuperficieParam } = useParams<{ id: string }>();
  const idSuperficie = idSuperficieParam ? parseInt(idSuperficieParam) : undefined;
  const isEdicao = !!idSuperficie;

  const { openNotification } = useNavigation();

  // FASE 05 — 2ª validação/PO 2026-07-24 (B4): empresa "apenas consulta" = somente-leitura.
  // FASE 05 — 6ª validação/PO 2026-08-11: bloqueia edição também quando o MODO EDIÇÃO está desligado.
  // Plano 14/10/2026 (Frente 4): planilha de superficies em andamento também deixa a tela somente-leitura.
  const somenteLeitura = useSomenteLeituraN1("superficies");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // FASE 05 — Ajustes 25/08 (item 4): SALVAR só habilita depois de alguma alteração.
  // Em cadastro novo não há estado inicial a comparar — o hook devolve sempre habilitado.
  const [tabIndex, setTabIndex] = useState<SuperficieTabIndex>(0);
  const [superficie, setSuperficie] = useState<Partial<ISuperficie>>({
    codigo: "",
    descricao: "",
    idSituacao: 1, // Em edição
    idStatus: 1, // Ativo
    // FASE 05 — 4ª validação/Frente 9 2026-07-30: Grupo (idGrupo) substitui a antiga categoria fixa.
    idGrupo: undefined,
    idUnidadeValorizacao: 0,
    precoVariavel: false,
  });

  // Listas de referência
  const [referencias, setReferencias] = useState<ISelectOption[]>([]);
  const [linhas, setLinhas] = useState<ISelectOption[]>([]);
  const [grupos, setGrupos] = useState<ISelectOption[]>([]);
  const [cores, setCores] = useState<ISelectOption[]>([]);
  const [unidadesValorizacao, setUnidadesValorizacao] = useState<ISelectOption[]>([]);
  const [tiposTecido, setTiposTecido] = useState<ISelectOption[]>([]);

  // Modais
  const [modalMidiasAberto, setModalMidiasAberto] = useState(false);
  const [modalExcluirAberto, setModalExcluirAberto] = useState(false);
  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);

  // FASE 05 — S3/PO 2026-05-13: carrega listas globais. Referencias filtrada por idTipoTela=4.
  // Linhas e carregada via useEffect quando idReferencia muda.
  const carregarListasReferencia = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        listarReferencias(ID_TIPO_TELA_SUPERFICIE),
        listarGrupos(),
        listarCores(),
        listarUnidadesValorizacao(),
        listarTiposTecido(),
      ]);

      const [
        refsResult,
        gruposResult,
        coresResult,
        unidadesResult,
        tiposTecidoResult,
      ] = results;

      const refsData = refsResult.status === 'fulfilled' ? refsResult.value : [];
      const gruposData = gruposResult.status === 'fulfilled' ? gruposResult.value : [];
      const coresData = coresResult.status === 'fulfilled' ? coresResult.value : [];
      const unidadesData = unidadesResult.status === 'fulfilled' ? unidadesResult.value : [];
      const tiposTecidoData = tiposTecidoResult.status === 'fulfilled' ? tiposTecidoResult.value : [];

      setReferencias(refsData);
      setGrupos(gruposData);
      setCores(coresData);
      setUnidadesValorizacao(unidadesData);
      setTiposTecido(tiposTecidoData);

      if (tiposTecidoResult.status === 'rejected') {
        console.error("Erro ao carregar tipos de tecido:", tiposTecidoResult.reason);
      }
    } catch (error) {
      console.error("Erro ao carregar listas de referência:", error);
    }
  }, []);

  // Carregar dados da superfície (se edição)
  const carregarSuperficie = useCallback(async () => {
    if (!idSuperficie) {
      setLoading(false);
      return;
    }

    try {
      // FASE 05 — 4ª validação/Frente 9 2026-07-30: idGrupo vem direto da API — sem depender
      // da lista de grupos para "traduzir" categoria.
      const superficieData = await obterSuperficiePorId(idSuperficie);

      if (superficieData) {
        setSuperficie(superficieData);
      } else {
        openNotification({ message: "Superfície não encontrada", type: "error" });
        navigate("/produtosN1");
      }
    } catch (error) {
      console.error("Erro ao carregar superfície:", error);
      openNotification({ message: "Erro ao carregar superfície", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [idSuperficie, navigate, openNotification]);

  useEffect(() => {
    carregarListasReferencia();
    carregarSuperficie();
  }, [carregarListasReferencia, carregarSuperficie]);

  // FASE 05 — S3+/PO 2026-05-13: validacao em tempo real de codigo duplicado.
  const { codigoDuplicado } = useValidacaoCodigoUnico(
    superficie.codigo,
    idSuperficie,
    validarCodigoUnico
  );

  // FASE 05 — S3/PO 2026-05-13: cascata Referencia -> Linha.
  useEffect(() => {
    if (superficie.idReferencia) {
      listarLinhas(superficie.idReferencia).then(setLinhas);
    } else {
      setLinhas([]);
    }
  }, [superficie.idReferencia]);

  // Handlers
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue as SuperficieTabIndex);
  };

  // FASE 05 — S3/PO 2026-05-13: limpar Linha ao trocar Referencia.
  const handleSuperficieChange = (campo: keyof ISuperficie, valor: any) => {
    setSuperficie((prev) => {
      const next: Partial<ISuperficie> = { ...prev, [campo]: valor };
      if (campo === "idReferencia") {
        next.idLinha = undefined;
      }
      return next;
    });
  };

  // Recarrega o cadastro após salvar, e o hook rearma o retrato sozinho — daí só `alterado`.
  const { alterado } = useFormularioAlterado(
    superficie,
    { ehNovo: !isEdicao, pronto: !loading }
  );

  const handleSalvar = async () => {
    // Validações básicas
    if (!superficie.codigo?.trim()) {
      openNotification({ message: "Código é obrigatório", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!superficie.descricao?.trim()) {
      openNotification({ message: "Descrição é obrigatória", type: "warning" });
      setTabIndex(0);
      return;
    }

    // FASE 05 — S3+/PO 2026-05-13: bloquear save se codigo ja cadastrado.
    // FASE 05 — 4ª validação/Frente 9 2026-07-30: mensagem longa padronizada (padrão do Componente).
    if (codigoDuplicado) {
      openNotification({
        message: "Este código de produto já existe. Informe um código diferente ou inclua o sufixo da sua empresa ou entre em contato com o departamento de Produto Informação da Alumisoft.",
        type: "warning",
      });
      setTabIndex(0);
      return;
    }

    // FASE 05 — 4ª validação/Frente 9 2026-07-30 (P2): Referência e Linha são obrigatórias.
    if (!superficie.idReferencia) {
      openNotification({ message: "Referência é obrigatória", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!superficie.idLinha) {
      openNotification({ message: "Linha é obrigatória", type: "warning" });
      setTabIndex(0);
      return;
    }

    // FASE 05 — 4ª validação/Frente 9 2026-07-30: Grupo (Cadastro de Grupos) substitui a categoria fixa.
    if (!superficie.idGrupo) {
      openNotification({ message: "Grupo é obrigatório", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!superficie.idCor) {
      openNotification({ message: "Cor é obrigatória", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!superficie.idUnidadeValorizacao) {
      openNotification({ message: "Un. Valorização é obrigatória", type: "warning" });
      setTabIndex(0);
      return;
    }

    // FASE 05 — 4ª validação/Frente 9 2026-07-30: condicionais decididos pela DESCRIÇÃO do
    // grupo selecionado (case-insensitive), não mais pela categoria fixa.
    const ehTelaRetratil = grupoTemDescricao(grupos, superficie.idGrupo, GRUPO_TELA_RETRATIL);
    const ehEsteiraMontada = grupoTemDescricao(grupos, superficie.idGrupo, GRUPO_ESTEIRA_MONTADA);

    // Validações condicionais - Grupo TELA RETRÁTIL
    if (ehTelaRetratil) {
      if (!superficie.idTipoTecido) {
        openNotification({ message: "Tipo tecido é obrigatório para o grupo TELA RETRÁTIL", type: "warning" });
        setTabIndex(0);
        return;
      }
      if (!superficie.larguraMinima || superficie.larguraMinima <= 0) {
        openNotification({ message: "Largura Mínima é obrigatória para o grupo TELA RETRÁTIL", type: "warning" });
        setTabIndex(0);
        return;
      }
      if (!superficie.larguraMaxima || superficie.larguraMaxima <= 0) {
        openNotification({ message: "Largura Máxima é obrigatória para o grupo TELA RETRÁTIL", type: "warning" });
        setTabIndex(0);
        return;
      }
      if (!superficie.alturaMinima || superficie.alturaMinima <= 0) {
        openNotification({ message: "Altura Mínima é obrigatória para o grupo TELA RETRÁTIL", type: "warning" });
        setTabIndex(0);
        return;
      }
      if (!superficie.alturaMaxima || superficie.alturaMaxima <= 0) {
        openNotification({ message: "Altura Máxima é obrigatória para o grupo TELA RETRÁTIL", type: "warning" });
        setTabIndex(0);
        return;
      }
    }

    // Validações condicionais - Grupo ESTEIRA MONTADA
    if (ehEsteiraMontada) {
      if (!superficie.peso || superficie.peso <= 0) {
        openNotification({ message: "Peso é obrigatório e deve ser maior que zero para o grupo ESTEIRA", type: "warning" });
        setTabIndex(0);
        return;
      }
      if (!superficie.passo?.trim()) {
        openNotification({ message: "Passo é obrigatório para o grupo ESTEIRA", type: "warning" });
        setTabIndex(0);
        return;
      }
      if (!superficie.terminal?.trim()) {
        openNotification({ message: "Terminal é obrigatório para o grupo ESTEIRA", type: "warning" });
        setTabIndex(0);
        return;
      }
    }

    setSaving(true);
    try {
      const dadosParaSalvar: Partial<ISuperficie> = {
        ...superficie,
      };

      const resultado = await salvarSuperficie(dadosParaSalvar);
      if (resultado?.sucesso) {
        openNotification({ message: "Superfície salva com sucesso", type: "success" });

        // Se era novo cadastro, redireciona para edição
        if (!isEdicao && resultado.id) {
          navigate(`/produtosN1/superficies/${resultado.id}`, { replace: true });
        } else if (isEdicao) {
          // FASE 05 — 6ª validação/PO 2026-08-11 (F3 Etapa 2): se o salvar congelou a versão de
          // mercado, a edição foi para uma NOVA cópia de trabalho — navegar para ela; senão a tela
          // recarregaria o registro publicado congelado em vez das alterações.
          if (resultado.id && String(resultado.id) !== String(idSuperficie)) {
            navigate(`/produtosN1/superficies/${resultado.id}`, { replace: true });
          } else {
            // Recarrega os dados
            carregarSuperficie();
          }
        }
      } else {
        // FASE 05 — 6ª validação/PO 2026-08-04 (item Superfície "Salvar não grava"): o servidor
        // recusou e a tela ficava muda (mesmo caso do Vidro/D5 na 4ª val.). Exibe a falha com a
        // mensagem real da API, em vez de "nenhum processo ocorre".
        openNotification({
          message: resultado?.mensagem || "Erro ao salvar superfície. Verifique os campos obrigatórios e tente novamente.",
          type: "error",
        });
      }
    } catch (error: any) {
      // getErrorsByApi (no service) LANÇA o erro com a mensagem real da API — antes o catch só
      // logava no console e a tela ficava muda. Agora a falha é exibida ao usuário.
      console.error("Erro ao salvar:", error);
      openNotification({
        message: error?.message || "Erro ao salvar superfície. Tente novamente.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicar = async () => {
    if (!idSuperficie) return;

    setSaving(true);
    try {
      const resultado = await duplicarSuperficie(idSuperficie);
      if (resultado?.sucesso && resultado.id) {
        openNotification({ message: "Superfície duplicada com sucesso", type: "success" });
        navigate(`/produtosN1/superficies/${resultado.id}`, { replace: true });
      }
    } catch (error) {
      console.error("Erro ao duplicar:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleExcluir = async () => {
    if (!idSuperficie) return;

    setSaving(true);
    try {
      const sucesso = await excluirSuperficie(idSuperficie);
      if (sucesso) {
        openNotification({ message: "Superfície excluída com sucesso", type: "success" });
        navigate("/produtosN1");
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleMidiasSave = (midias: {
    imagemUrl?: string;
    imagemNomeArquivo?: string;
  }, idAlvo?: number) => {
    setSuperficie((prev) => ({
      ...prev,
      imagemUrl: midias.imagemUrl,
      imagemNomeArquivo: midias.imagemNomeArquivo,
    }));
    // Plano 21/09/2026 (Frente 3): superfície Disponível → a mídia foi para a cópia de trabalho;
    // navegar para ela (mesmo tratamento do handleSalvar quando o espelho cria a cópia).
    if (idAlvo && String(idAlvo) !== String(idSuperficie)) {
      navigate(`/produtosN1/superficies/${idAlvo}`, { replace: true });
    }
  };

  const handleVoltar = () => {
    navigate("/produtosN1");
  };

  const renderTabContent = () => {
    switch (tabIndex) {
      case 0: // GERAL
        return (
          <TabGeral
            superficie={superficie}
            onChange={handleSuperficieChange}
            referencias={referencias}
            linhas={linhas}
            grupos={grupos}
            cores={cores}
            unidadesValorizacao={unidadesValorizacao}
            tiposTecido={tiposTecido}
            coresTecido={cores} // Usar a mesma lista de cores para cor do tecido
            codigoDuplicado={codigoDuplicado}
            onOpenMidias={() => setModalMidiasAberto(true)}
          />
        );
      // FASE 05 — 4ª validação/Frente 9 2026-07-30: aba "Dimensões" removida (relatório do PO).
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DashboardStackCustom sx={{ width: "100%" }} pt={2}>
      <Grid container display={"flex"} alignItems={"center"}>
        {/* Header: Título + Breadcrumb - Fora do StackCustom para ter cor de fundo da página */}
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5} sx={{ width: "100%", px: 4.5 }}>
          <IconButton onClick={handleVoltar} size="small" sx={{ color: "#666" }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ color: "#333", fontWeight: 400, fontSize: "1.8rem" }}>
            Cadastro de Superfície
          </Typography>
        </Stack>
        
        {/* Breadcrumb abaixo do título */}
        <Box sx={{ 
          borderBottom: "2px solid #3C90AD", 
          pb: 1.5, 
          mb: 2,
          ml: 4.5,
          width: "100%",
        }}>
          <Breadcrumbs separator="›" sx={{ mt: 0 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Home
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/produtosN1"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Cadastros
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/produtosN1"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Superfícies
            </Link>
            <Typography color="text.primary" sx={{ fontSize: "0.75rem" }}>
              {isEdicao ? "Editar" : "Nova Superfície"}
            </Typography>
          </Breadcrumbs>
        </Box>

        <StackCustom>
          
          {/* Última atualização e Log */}
          {isEdicao && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, gap: 2 }}>
              {superficie.dataUltimaAtualizacao && formatarDataAtualizacao(superficie.dataUltimaAtualizacao) && (
                <Typography variant="body2" color="text.secondary">
                  Última atualização: {formatarDataAtualizacao(superficie.dataUltimaAtualizacao)}
                </Typography>
              )}
              <Link
                component="button"
                variant="body2"
                onClick={() => setModalHistoricoAberto(true)}
                sx={{ color: "#3C90AD" }}
              >
                Log de atualizações
              </Link>
            </Box>
          )}

          <AvisoPlanilhaPendenteN1 tipoMaterial="superficies" />

          {/* Abas */}
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              mb: 3,
              minHeight: "auto",
              "& .MuiTabs-flexContainer": {
                gap: "2px",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                minHeight: "52px",
                backgroundColor: "#7ab8cc",
                color: "#fff",
                fontSize: "0.75rem",
                "&.Mui-selected": {
                  backgroundColor: "#3C90AD",
                  color: "#fff",
                },
              },
            }}
          >
            {/* FASE 05 — 4ª validação/Frente 9 2026-07-30: aba "Dimensões" removida (relatório do PO). */}
            <Tab label="GERAL" />
          </Tabs>

          {/* Conteúdo das Abas — bloqueado quando somente-leitura (B4). */}
          <Box
            sx={{
              flexGrow: 1,
              pb: 4,
              ...(somenteLeitura && { opacity: 0.7, pointerEvents: "none" }),
            }}
          >
            {renderTabContent()}
          </Box>
        </StackCustom>

        {/* Footer com botões de ação — oculto em somente-leitura (B4). */}
        {!somenteLeitura && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            mt: 3,
            width: "100%",
          }}
        >
          {isEdicao && (
            <>
              <Button
                variant="text"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setModalExcluirAberto(true)}
                disabled={saving}
              >
                EXCLUIR
              </Button>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleDuplicar}
                disabled={saving}
                sx={{ borderColor: "#3C90AD", color: "#3C90AD" }}
              >
                DUPLICAR
              </Button>
            </>
          )}
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSalvar}
            disabled={saving || !alterado}
            sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
          >
            SALVAR
          </Button>
        </Box>
        )}
      </Grid>

      {/* Modais */}
      <ModalGerenciamentoMidias
        open={modalMidiasAberto}
        onClose={() => setModalMidiasAberto(false)}
        superficie={superficie}
        onSave={handleMidiasSave}
        onRefreshSuperficie={carregarSuperficie}
      />
      <ModalConfirmacaoExcluir
        open={modalExcluirAberto}
        onClose={() => setModalExcluirAberto(false)}
        onConfirm={handleExcluir}
        codigo={superficie.codigo || ""}
        descricao={superficie.descricao || ""}
      />
      <ModalHistorico
        open={modalHistoricoAberto}
        onClose={() => setModalHistoricoAberto(false)}
        superficieId={idSuperficie}
        superficieCodigo={superficie.codigo || ""}
      />
    </DashboardStackCustom>
  );
};
